import {
  Button,
  confirm,
  FormInput,
  FormRoot,
  Modal,
  RHF,
  toast,
  zodResolver,
} from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Edit, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { z } from "zod";
import { config } from "@/shared/lib/config";

const FlowStepTypeFormSchema = z.object({
  name: z.string().min(1, "ກະລຸນາໃສ່ຊື່ປະເພດຂັ້ນຕອນ"),
  orderIndex: z.preprocess((val) => {
    if (val === "" || val === null || val === undefined) return 0;
    const num = typeof val === "string" ? Number(val) : val;
    return typeof num === "number" && !Number.isNaN(num) ? Math.floor(num) : 0;
  }, z.number().int().min(0).default(0)),
});

type FlowStepTypeFormValues = z.infer<typeof FlowStepTypeFormSchema>;

type FlowStepType = {
  id: string;
  name: string;
  orderIndex: number;
  isActive: boolean;
};

export function FlowStepTypeManagementModal({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const qc = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const { data: stepTypes, isLoading } = useQuery<{ data: FlowStepType[] }>({
    queryKey: ["flows-step-types-list"],
    queryFn: async () => {
      const res = await fetch(`${config.apiUrl}/flows/step-types`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to fetch step types");
      return res.json();
    },
    enabled: open,
  });

  const createMutation = useMutation({
    mutationFn: async (data: FlowStepTypeFormValues) => {
      const res = await fetch(`${config.apiUrl}/flows/step-types`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
        credentials: "include",
      });
      if (!res.ok) {
        const error = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error(error.error || "Failed to create step type");
      }
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["flows-step-types-list"] });
      qc.invalidateQueries({ queryKey: ["flows-step-types"] });
      toast.success("ສ້າງປະເພດຂັ້ນຕອນສໍາເລັດ");
      setIsCreating(false);
    },
    onError: (error: Error) => {
      toast.error(error.message || "ສ້າງປະເພດຂັ້ນຕອນລົ້ມເຫຼວ");
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: FlowStepTypeFormValues;
    }) => {
      const res = await fetch(`${config.apiUrl}/flows/step-types/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
        credentials: "include",
      });
      if (!res.ok) {
        const error = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error(error.error || "Failed to update step type");
      }
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["flows-step-types-list"] });
      qc.invalidateQueries({ queryKey: ["flows-step-types"] });
      toast.success("ແກ້ໄຂປະເພດຂັ້ນຕອນສໍາເລັດ");
      setEditingId(null);
    },
    onError: (error: Error) => {
      toast.error(error.message || "ແກ້ໄຂປະເພດຂັ້ນຕອນລົ້ມເຫຼວ");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${config.apiUrl}/flows/step-types/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) {
        const error = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error(error.error || "Failed to delete step type");
      }
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["flows-step-types-list"] });
      qc.invalidateQueries({ queryKey: ["flows-step-types"] });
      toast.success("ລຶບປະເພດຂັ້ນຕອນສໍາເລັດ");
    },
    onError: (error: Error) => {
      toast.error(error.message || "ລຶບປະເພດຂັ້ນຕອນລົ້ມເຫຼວ");
    },
  });

  const handleDelete = async (type: FlowStepType) => {
    if (
      await confirm({
        title: `ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບປະເພດ "${type.name}"?`,
        description: "ປະເພດຂັ້ນຕອນນີ້ຈະຖືກລຶບອອກ",
        actionText: "ຢືນຢັນ",
        cancelText: "ຍົກເລີກ",
      })
    ) {
      await deleteMutation.mutateAsync(type.id);
    }
  };

  const editingType = editingId
    ? stepTypes?.data.find((t) => t.id === editingId)
    : null;

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="ຈັດການປະເພດຂັ້ນຕອນ"
      size="lg"
    >
      <div className="space-y-4">
        <div className="flex justify-end">
          <Button onClick={() => setIsCreating(true)} size="sm">
            <Plus className="mr-2 h-4 w-4" />
            ເພີ່ມປະເພດໃໝ່
          </Button>
        </div>

        {isCreating && (
          <StepTypeForm
            onSubmit={(vals) => {
              createMutation.mutate(vals);
            }}
            onCancel={() => setIsCreating(false)}
            submitting={createMutation.isPending}
          />
        )}

        {editingType && (
          <StepTypeForm
            initialValues={{
              name: editingType.name,
              orderIndex: editingType.orderIndex,
            }}
            onSubmit={(vals) => {
              if (editingId) {
                updateMutation.mutate({ id: editingId, data: vals });
              }
            }}
            onCancel={() => setEditingId(null)}
            submitting={updateMutation.isPending}
          />
        )}

        {isLoading ? (
          <div className="py-8 text-center text-muted-foreground">
            ກຳລັງໂຫຼດ...
          </div>
        ) : stepTypes?.data.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground">
            ຍັງບໍ່ມີປະເພດຂັ້ນຕອນ
          </div>
        ) : (
          <div className="space-y-2">
            {stepTypes?.data.map((type) => (
              <div
                key={type.id}
                className="flex items-center justify-between rounded-lg border p-3"
              >
                <div>
                  <div className="font-medium">{type.name}</div>
                  <div className="text-muted-foreground text-xs">
                    ລຳດັບ: {type.orderIndex}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingId(type.id)}
                    disabled={!!editingId || isCreating}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(type)}
                    disabled={
                      !!editingId || isCreating || deleteMutation.isPending
                    }
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}

function StepTypeForm({
  initialValues,
  onSubmit,
  onCancel,
  submitting,
}: {
  initialValues?: Partial<FlowStepTypeFormValues>;
  onSubmit: (values: FlowStepTypeFormValues) => void;
  onCancel: () => void;
  submitting?: boolean;
}) {
  const methods = RHF.useForm<FlowStepTypeFormValues>({
    resolver: zodResolver(FlowStepTypeFormSchema) as any,
    defaultValues: {
      name: initialValues?.name ?? "",
      orderIndex: initialValues?.orderIndex ?? 0,
    },
  });

  return (
    <FormRoot<FlowStepTypeFormValues>
      methods={methods}
      onSubmit={onSubmit}
      className="space-y-4 rounded-lg border p-4"
    >
      <FormInput
        name="name"
        label="ຊື່ປະເພດຂັ້ນຕອນ"
        requiredMark
        placeholder="ຕົວຢ່າງ: ວຽກ, ກວດສອບ, ອະນຸມັດ"
      />
      <FormInput name="orderIndex" label="ລຳດັບ" type="number" placeholder="0" />
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          ຍົກເລີກ
        </Button>
        <Button type="submit" disabled={submitting}>
          {submitting ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
        </Button>
      </div>
    </FormRoot>
  );
}
